import { useState, useEffect, useMemo } from 'react';
import { useUserContext } from '@context/UserContext';
import { useNavigate } from 'react-router-dom';
import { useUserManagement } from '@hooks/useUserManagement';
import { useCustomFields } from '@hooks/useNewUserCustomFields';
import { useUserValidation } from '@hooks/useUserValidation';
import { useAccountType } from '@hooks/useAccountType';
import type { User } from 'types/UserZendesk';
import { formatCNPJ, formatPhone, formateCpf } from '@hooks/masks';

export function useAddAccount() {
  const {
    selectedRole,
    setSelectedUser,
    newUserFormData,
    setNewUserFormData,
    userFields,
    customFormFields,
    currentTipoConta,
    setCurrentTipoConta
  } = useUserContext();

  const navigate = useNavigate();
  const [newUser, setNewUser] = useState(newUserFormData);
  const [userAlcada, setUserAlcada] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const {
    searchTerm,
    setSearchTerm,
    users,
    loading,
    searchUsersHandler,
    selectExistingUser,
    noUsers
  } = useUserManagement();

  const {
    customFields,
    handleCustomFieldChange,
    getVisibleFields,
    tipoContas
  } = useCustomFields();

  const {
    cpfError,
    emailError,
    cnpjError,
    existUser,
    existUserEmail,
    handleCpfChange,
    handleEmailChange,
    handleCnpjChange,
    isCpfComplete,
    isCnpjComplete,
    existUserCpfFormatted
  } = useUserValidation();

  const { isLegalEntity } = useAccountType(selectedRole);

  const visibleFields = useMemo(
    () => getVisibleFields(selectedRole, isLegalEntity),
    [userFields, selectedRole, isLegalEntity, getVisibleFields]
  );


  const isTransportadora = selectedRole === 'transportadora' || selectedRole === 'revenda';
  const isJuizAndAdvogado = selectedRole === 'juiz' || selectedRole === 'advogado';
  const isPosto = selectedRole === 'posto';
  const isProcon = selectedRole === 'procon';
  const isCpfCompleted = isCpfComplete;
  const isCnpjCompleted = newUserFormData.cnpj_regex.length === 18 || newUserFormData.cnpj_regex.length === 14 || isCnpjComplete;

  const oabFieldKey = selectedRole === 'advogado' ? userFields.find(field => field.key === '_advogado__oab')?.key : null;
  const oabKey = userFields.find(field => field.key === '_advogado__oab')?.key;
  const hasOab = selectedRole === 'advogado' && oabKey && !!customFields[oabKey] && customFields[oabKey].toString().trim().length > 0;
  const oabValue = oabFieldKey ? customFields[oabFieldKey] : null;
  const isOabEmpty = selectedRole === 'advogado' && (!oabValue || oabValue.toString().trim() === '');

  const hasCpf = !!newUserFormData.cpf_3 && newUserFormData.cpf_3.replace(/\D/g, '').length > 0;
  const nameValue = newUserFormData.name?.trim() || '';
  const isNameIncomplete =
    nameValue.length > 0 && nameValue.split(' ').filter(Boolean).length < 2;
  const isFullNameRequired = (hasCpf || hasOab) && (!nameValue || isNameIncomplete); 

  const linhasAtendimentoKey = userFields.find(field => field.key === '_assistencia_tecnica__linhas_atendidas')?.key;

  const razaoSocialKey = userFields.find(field => field.key === '_pessoa_juridica__razao_social')?.key;
  const razaoEmpty =
    selectedRole !== 'advogado' &&
    selectedRole !== 'revenda' &&
    selectedRole !== 'posto' &&
    tipoContas !== 'pessoa_fisica' &&
    (
      !razaoSocialKey ||
      !customFields[razaoSocialKey] ||
      customFields[razaoSocialKey].toString().trim() === ''
    );
    
  const linhasAtendimentoEmpty = selectedRole === 'posto' && (
    !linhasAtendimentoKey ||
    !customFields[linhasAtendimentoKey] ||
    customFields[linhasAtendimentoKey].toString().trim() === ''
  );

const isButtonDisabled =
  !!cpfError ||
  !!emailError ||
  !!cnpjError ||
  isOabEmpty ||

  (tipoContas === 'pessoa_fisica' && !isCpfCompleted) ||
  (tipoContas === 'pessoa_juridica' && !isCnpjCompleted) ||

  (isJuizAndAdvogado && !isCpfComplete) ||
  (isTransportadora && !isCnpjCompleted) ||
  (isPosto && !isCnpjComplete) ||
  (isProcon && !isCnpjCompleted) ||

  isFullNameRequired ||
  razaoEmpty

  const invalidFields = {
    cpf_3: !!cpfError ||
      (tipoContas === 'pessoa_fisica' && !isCpfComplete) ||
      (selectedRole === 'advogado' && !isCpfComplete),
    cnpj_regex: !!cnpjError ||
      (
        (tipoContas === 'pessoa_juridica' || isTransportadora || isPosto || isProcon)
        && !isCnpjCompleted
      ),
    name: isFullNameRequired,
    _advogado__oab: isOabEmpty,
    _pessoa_juridica__razao_social: razaoEmpty,
    _assistencia_tecnica__linhas_atendidas: linhasAtendimentoEmpty,
  };

  function onCopy(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();

    const inputValue = (event.target as HTMLInputElement).value;
    const valorLimpo = inputValue.replace(/\D/g, '');

    event.clipboardData.setData('text/plain', valorLimpo);
  }
  useEffect(() => {
    setNewUserFormData(newUser);
  }, [newUser, setNewUserFormData]);

  useEffect(() => {
    if (newUser.cpf_3) {
      handleCpfChange(newUser.cpf_3);
    }
  }, [newUser.cpf_3, handleCpfChange, tipoContas]);

  useEffect(() => {
    if (selectedRole === 'revenda' || selectedRole === 'posto') {
      setCurrentTipoConta('pessoa_juridica');
    } else if (tipoContas && tipoContas !== currentTipoConta) {
      setCurrentTipoConta(tipoContas);
    }
  }, [tipoContas, currentTipoConta, setCurrentTipoConta, selectedRole]);

  const prepareNewUser = () => {
    if (tipoContas) {
      setCurrentTipoConta(tipoContas);
    }

    const tipoContaToSet = (selectedRole === 'revenda' || selectedRole === 'posto') ? 'pessoa_juridica' : tipoContas;
    setSelectedUser({
      name: newUserFormData.name ?? customFormFields['[Geral] Cognome'] ?? customFormFields['[Geral] Razão social'] ?? newUserFormData.email.split('@')[0],
      email: newUserFormData.email,
      phone: newUserFormData.phone,
      user_fields: {
        ...customFormFields,
        cpf_3: newUserFormData.cpf_3.replace(/\D/g, ''),
        cnpj_regex: newUserFormData.cnpj_regex.replace(/\D/g, ''),
        alcada_do_usuario: userAlcada,
        tipo_de_conta: tipoContaToSet
      },
      role: selectedRole ?? '',
    } as unknown as User);
    navigate('/review-user-account');
  };
  const handleInputChange = async (
    field: keyof typeof newUser,
    value: string
  ) => {
    if (field === 'cpf_3') {
      const rawValue = value.replace(/\D/g, '').slice(0, 11);
      const formattedValue = formateCpf(rawValue);
      await handleCpfChange(rawValue);
      setNewUser(prev => ({ ...prev, cpf_3: formattedValue }));
    } else if (field === 'email') {
      await handleEmailChange(value);
      setNewUser(prev => ({ ...prev, email: value }));
    } else if (field === 'phone') {
      const formattedValue = formatPhone(value);
      setNewUser(prev => ({ ...prev, phone: formattedValue }));
    } else if (field === 'cnpj_regex') {
      const rawValue = value.replace(/\D/g, '');
      const formattedValue = formatCNPJ(rawValue);
      await handleCnpjChange(rawValue);
      setNewUser(prev => ({ ...prev, cnpj_regex: formattedValue }));
    }
    else {
      setNewUser(prev => ({ ...prev, [field]: value }));
    }
  };
  return {
    searchTerm,
    setSearchTerm,
    users,
    loading,
    newUser,
    setNewUser,
    searchUsers: searchUsersHandler,
    prepareNewUser,
    selectedRole,
    selectExistingUser: (user: User) => selectExistingUser(user, selectedRole ?? ''),
    cpfError,
    handleInputChange,
    navigate,
    existUser,
    emailError,
    existUserEmail,
    visibleFields,
    customFields,
    handleCustomFieldChange,
    isLegalEntity,
    setUserAlcada,
    isCpfComplete,
    noUsers,
    tipoContas,
    isButtonDisabled,
    cnpjError,
    invalidFields,
    showErrors,
    setShowErrors,
    onCopy,
    existUserCpfFormatted,
    setSelectedUser
  };
}